import { useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useDemoRole } from '../../../contexts/DemoRoleContext';
import { useUserOrganizations } from './useOrganization';
import { membersFromOrgDoc } from '../../../services/roleService';
import { PERSONA_TO_ROLE } from '../utils/conditionWorkflow';

/**
 * The person acting on OE Conditions right now, in the shape `conditionWorkflow.js` expects:
 *   { uid, name, role, isAdmin, nwtId, deptId, siteId, orgId, org, members, routing, actingFor }
 * Under View As, `uid`/`role`/scope are the member's and `actingFor` records the member for the
 * audit trail (P4-07) while `enteredBy` stays the keyboard user.
 */
export function useConditionViewer() {
  const { user } = useAuth();
  const { personaUser, queryScope, personaId, viewingAs } = useDemoRole();
  const { organizations } = useUserOrganizations(user?.uid);
  const org = organizations[0] || null;

  return useMemo(() => {
    const uid = personaUser?.id || queryScope?.userId || user?.uid || null;
    const role = viewingAs?.role || PERSONA_TO_ROLE[personaId] || 'respondent';
    const isAdmin = personaId === 'admin' || personaId === 'ppi';
    const members = org ? membersFromOrgDoc(org) : [];
    const me = members.find((m) => m.uid === uid) || null;
    const actingFor = user?.uid && uid && uid !== user.uid ? { uid, name: personaUser?.name || me?.displayName || '' } : null;
    return {
      uid,
      name: personaUser?.name || me?.displayName || user?.displayName || user?.email || '',
      role,
      isAdmin,
      nwtId: queryScope?.nwtId || null,
      deptId: queryScope?.deptId || null,
      siteId: queryScope?.siteId || null,
      orgId: org?.id || queryScope?.organizationId || null,
      org,
      members,
      routing: org?.reviewRouting || null,
      actingFor,
      keyboardUid: user?.uid || null,
      keyboardName: user?.displayName || user?.email || '',
    };
  }, [user?.uid, user?.displayName, user?.email, personaUser?.id, personaUser?.name, queryScope?.userId, queryScope?.nwtId, queryScope?.deptId, queryScope?.siteId, queryScope?.organizationId, personaId, viewingAs?.role, org]);
}

export default useConditionViewer;
